import { useMutation, useQueryClient } from '@tanstack/react-query'

import { TCampaign } from '~/features/campaigns/models'
import { queryKeys } from '~/features/campaigns/utils'
import { EHttpService, EServerPath } from '~/models/api'
import HttpService from '~/services/http'
import { HttpServiceBaseResponse } from '~/services/http/models'
import pathBuilder from '~/utils/api'

export type TAdvanceCampaignTimeResponse = {
  /** ISO date the campaign's logical clock moved to. */
  campaignDate: string
  /** The checkpoint executed — where the campaign clock now sits. Null when nothing pending. */
  advancedTo: string | null
  enqueuedOutreachIds: string[]
  enqueuedCount: number
  remainingPendingCount: number
  message?: string
}

/**
 * Growth executes these asynchronously off SQS, so a successful response means "enqueued",
 * not "sent". Each contact then runs an AI evaluation and a follow-up generation, so outcomes
 * land over tens of seconds.
 *
 * These refetches are for the contact rows — attempts, progress, next attempt. Staggered rather
 * than a polling loop: three cheap refetches cover cold start through a slow LLM round trip,
 * with no interval to tear down and no request that keeps firing forever.
 */
const ASYNC_EXECUTION_REFETCH_DELAYS_MS = [5000, 15000, 30000]

const advanceCampaignTime = (id: string) => {
  return HttpService.post<TAdvanceCampaignTimeResponse>(
    { path: pathBuilder(EServerPath.campaignAdvanceTime, { id }) },
    EHttpService.internal,
  )
}

export default function useAdvanceCampaignTime(id: string) {
  const queryClient = useQueryClient()

  const invalidateCampaignViews = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.campaignContacts(id),
    })
    // Prefix match: the paginated key carries filter/sort args this hook has no view of,
    // so invalidating one hardcoded variant would leave the visible table stale.
    queryClient.invalidateQueries({
      queryKey: [EServerPath.campaignContactsPaginated, id],
    })
    // The checkpoint has moved, so a reopened dialog must not show the one just consumed.
    queryClient.invalidateQueries({
      queryKey: queryKeys.campaignAdvanceTimePreview(id),
    })
  }

  return useMutation({
    mutationFn: () => advanceCampaignTime(id),
    onSuccess: (response) => {
      const advancedTo = response?.data?.advancedTo

      // Move the displayed clock straight away from the response we already hold. Refetching
      // the campaign instead would read `MAX(executedAt)`, which Growth has not written yet —
      // so the server would answer with wall clock and the label would appear not to move.
      // The campaign query is deliberately left out of the invalidations below for the same
      // reason: it would drag the label back until execution lands.
      if (advancedTo) {
        queryClient.setQueryData<HttpServiceBaseResponse<TCampaign>>(
          queryKeys.campaign(id),
          (previous) =>
            previous?.data
              ? {
                  ...previous,
                  data: { ...previous.data, campaignDate: advancedTo },
                }
              : previous,
        )
      }

      // Immediately, to pick up the pending -> executing transition.
      invalidateCampaignViews()
      // Then again as Growth works through the batch. Safe after unmount: invalidateQueries
      // acts on the query client, not this component.
      ASYNC_EXECUTION_REFETCH_DELAYS_MS.forEach((delay) => {
        setTimeout(invalidateCampaignViews, delay)
      })
    },
  })
}
